/**
 * `<TenantStatusModal>` — sub-modal for changing a candidate's tenant status.
 *
 * Shows the 5-value enum as a segmented / radio list; current value is
 * visually checked.  On "Save" with a terminal outcome AND N > 0 in-flight
 * pairings, the parent opens `<CascadePromptModal>` next.  Otherwise the
 * status change applies immediately via `useTenantStatusMutation`.
 *
 * This component handles: step 1 — which status? (user picks and saves).
 * The cascade decision (step 2) is handled by `<CascadePromptModal>` in the
 * parent.
 *
 * Spec: candidate-detail-rebuild-spec.md §7.1 / global-patterns §4.1.
 */
import { useState, type JSX } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/common/Button";
import type { TenantStatus } from "@/lib/candidatesApi";

const STATUS_OPTIONS: Array<{ value: TenantStatus; label: string; description: string }> = [
  { value: "available",   label: "Available",   description: "Actively considered for roles" },
  { value: "hired",       label: "Hired",        description: "Hired at your organization" },
  { value: "rejected",    label: "Rejected",     description: "Not moving forward" },
  { value: "opted_out",   label: "Opted Out",    description: "Candidate withdrew" },
  { value: "unavailable", label: "Unavailable",  description: "Temporarily not available" },
];

interface TenantStatusModalProps {
  currentStatus: TenantStatus;
  candidateName: string;
  isPending: boolean;
  onSave: (newStatus: TenantStatus) => void;
  onCancel: () => void;
}

export function TenantStatusModal({
  currentStatus,
  candidateName,
  isPending,
  onSave,
  onCancel,
}: TenantStatusModalProps): JSX.Element {
  const [selected, setSelected] = useState<TenantStatus>(currentStatus);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  const firstName = candidateName.split(" ")[0] ?? candidateName;
  const changed = selected !== currentStatus;

  return (
    <div
      className="pointer-events-auto fixed inset-0 z-50 flex items-center justify-center bg-surface-overlay backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tenant-status-modal-title"
      onKeyDown={handleKeyDown}
      onClick={onCancel}
      data-testid="tenant-status-modal"
    >
      <div
        className="w-full max-w-sm rounded-xl border border-border-default bg-surface-elevated p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="tenant-status-modal-title"
          className="mb-1 text-lg font-semibold text-text-emphasis"
        >
          Change {firstName}&apos;s status
        </h2>
        <p className="mb-5 text-sm text-text-secondary">
          This is {firstName}&apos;s tenant-wide standing across all jobs.
        </p>

        <fieldset className="mb-6 flex flex-col gap-2" aria-label="Select status">
          {STATUS_OPTIONS.map((opt) => {
            const isSelected = opt.value === selected;
            return (
              <label
                key={opt.value}
                className={
                  "flex cursor-pointer items-center justify-between rounded-lg border px-4 py-3 transition-colors " +
                  (isSelected
                    ? "border-border-focus bg-surface-subtle"
                    : "border-border-default bg-surface-card hover:bg-surface-card-hover")
                }
                data-testid={`status-option-${opt.value}`}
              >
                <div>
                  <span className="text-sm font-medium text-text-primary">
                    {opt.label}
                  </span>
                  <p className="text-xs text-text-secondary">{opt.description}</p>
                </div>
                <input
                  type="radio"
                  name="tenant-status"
                  value={opt.value}
                  checked={isSelected}
                  onChange={() => setSelected(opt.value)}
                  className="sr-only"
                  autoFocus={isSelected}
                />
                {isSelected ? (
                  <Check
                    size={16}
                    className="flex-shrink-0 text-accent-secondary"
                    aria-hidden
                  />
                ) : null}
              </label>
            );
          })}
        </fieldset>

        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isPending}
            data-testid="tenant-status-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!changed || isPending}
            loading={isPending}
            onClick={() => onSave(selected)}
            data-testid="tenant-status-save"
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
